import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { hasValidRutDigits } from "@/lib/rut";
import { resolveIssueDateForPrefill } from "@/lib/invoice-prefill-issue-date";
import { todayInUruguay, tomorrowInUruguay, parseLocalDateOnly } from "@/lib/dateFormat";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Loader2, ChevronDown, CalendarClock } from "lucide-react";

import { AddClientDialog } from "@/components/features/clients/AddClientDialog";
import {
  InvoiceLineCatalogPicker,
  type CatalogPickerItem,
} from "@/components/features/invoices/InvoiceLineCatalogPicker";
import { listProducts } from "@/services/productService";
import { listBrands, updateBrand } from "@/services/brandService";
import { BrandSelect } from "@/components/features/brands/BrandSelect";
import { AdendaField } from "@/components/features/adenda/AdendaField";
import { composeAdendaPayload } from "@/pages/invoices/adenda-payload";
import { brandKeys } from "@/lib/queryKeys";
import {
  findTaxOptionByCategoryAndRate,
  resolveCatalogItemRate,
  taxOptionsForRegime,
  type SpecialRegime,
} from "@/lib/taxOptions";
import { ScheduleSendDialog } from "@/components/features/invoices/ScheduleSendDialog";
import { DatePickerField } from "@/components/shared/DatePickerField";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatMoney } from "@/lib/currencyFormat";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  invoiceCreationBlockedEmptyExplanationKey,
  useInvoiceCreationBlocked,
} from "@/hooks/useInvoiceCreationBlocked";
import { useIssuableCfeTypes } from "@/hooks/useIssuableCfeTypes";
import {
  lineCurrencyFromClientDefault,
  paymentMethodFromPaymentTermDays,
} from "@/lib/client-invoice-defaults";
import { CLIENT_SELECT_ADD_NEW_VALUE } from "@/lib/client-select-constants";
import { clientKeys, productKeys } from "@/lib/queryKeys";
import { dashboardKeys } from "@/lib/queryKeys";
import { invoiceKeys } from "@/lib/queryKeys";
import { tenantKeys } from "@/lib/queryKeys";
import { useAuth } from "@/contexts/AuthContext";
import { getTenantCurrent, updateTenantAdenda } from "@/services/authService";
import { getRucInfoForClient, listClients, type Client } from "@/services/clientService";
import { listClientCatalogItems, type ClientCatalogItem } from "@/services/clientCatalogService";
import { getExchangeRates } from "@/services/dashboardService";
import {
  buildPayload,
  normalizeRut,
  type CfeType,
  type LineForm,
  type ReceiverForm,
} from "@/lib/invoice-create-payload";
import {
  getLineTaxAmount,
  isPriceWithoutVat,
  TAX_RATE_BY_CATEGORY,
  getLineTaxAmountAtRate,
} from "@/lib/invoice-line-tax";
import {
  createInvoice,
  createDraftInvoice,
  updateDraftInvoice,
  deleteDraftInvoice,
  downloadInvoicePdf,
  getInvoice,
  previewInvoicePdf,
  scheduleInvoiceSend,
} from "@/services/invoiceService";
import type { CreateInvoiceRequest } from "@/services/invoiceService";
import {
  CONSUMER_PLACEHOLDER_NAME,
  CONSUMER_PLACEHOLDER_RUT,
  getEticketThresholdUyu,
} from "@fiscalo/shared-types";
import type { InvoiceLine, TaxCategory } from "@fiscalo/shared-types";
import { trackValidationBlocked } from "@/lib/analytics/friction";

type ClientLocation = "uruguay" | "export";

const COUNTRY_OPTIONS: { value: string; label: string }[] = [
  { value: "AR", label: "Argentina" },
  { value: "BR", label: "Brasil" },
  { value: "US", label: "Estados Unidos" },
  { value: "CL", label: "Chile" },
  { value: "PY", label: "Paraguay" },
  { value: "ES", label: "España" },
  { value: "DE", label: "Alemania" },
  { value: "CN", label: "China" },
  { value: "MX", label: "México" },
  { value: "OTHER", label: "Otro" },
];

/**
 * Re-tile a line under taxCategory='exempt' while preserving the
 * line's existing financial total. Used in two places:
 *
 * 1. Reissue prefill for a Literal E tenant whose original CFE was
 *    issued pre-IVA-lock with vat_basic + an IVA amount. The accountant
 *    is re-emitting the same charge to the same client — the
 *    client-owed total must not silently drop.
 * 2. The forceExemptTax effect when tenant data arrives after the
 *    form has any non-trivial lines (race after reissue prefill,
 *    catalog auto-apply, etc.).
 *
 * Rolls the per-line taxAmount back into unitPrice by spreading it
 * across the line's quantity, then zeroes the tax. e.g. q=2,
 * unitPrice=50, taxAmount=22 → q=2, unitPrice=61, taxAmount=0
 * (line total stays at 122). Already-exempt lines pass through
 * untouched.
 */
function normalizeLineForExempt(line: LineForm): LineForm {
  if (line.taxCategory === "exempt") return line;
  const safeQuantity = line.quantity && line.quantity > 0 ? line.quantity : 1;
  const taxPerUnit = (line.taxAmount || 0) / safeQuantity;
  const newUnitPrice =
    Math.round(((line.unitPrice || 0) + taxPerUnit) * 100) / 100;
  return {
    ...line,
    taxCategory: "exempt",
    unitPrice: newUnitPrice,
    taxAmount: 0,
  };
}

function getCfeType(
  clientLocation: ClientLocation,
  needRut: boolean
): CfeType {
  // Foreign receivers ship as e-Ticket (101) with the export-specific
  // fields (countryCode + docRecepExt). DGI's catalog would also accept
  // 121 e-Factura Exportación, but the business uses e-Ticket for
  // services to foreign clients (typically IRAE-exempt services under
  // literal S, Artículo 52, Título 4 — see the per-line legend). Keeping
  // exports on 101 also avoids needing a separate 121 CAE per tenant.
  if (clientLocation === "export") return "101";
  return needRut ? "111" : "101";
}

function getHumanTypeKey(cfeType: CfeType): string {
  switch (cfeType) {
    case "101":
      return "invoices.create.typeFinalConsumer";
    case "111":
      return "invoices.create.typeDomesticInvoice";
    case "121":
      return "invoices.create.typeExportInvoice";
    default:
      return "";
  }
}

/** Minimal invoice data to pre-fill the new-invoice form when re-issuing a rejected invoice. */
export interface ReissueFromInvoice {
  cfeType: "101" | "111" | "121";
  receiverSnapshot: CreateInvoiceRequest["receiverSnapshot"];
  lines: InvoiceLine[];
  currency: "UYU" | "USD";
  customerId?: string;
  exchangeRate?: number;
  /** Original issueDate (YYYY-MM-DD) — preserved on reissue so the
   *  fiscal period of the rejected CFE is honoured. */
  issueDate?: string;
}

interface LocationStateWithClient {
  client?: Client;
  reissueFrom?: ReissueFromInvoice;
  /** When list does not have full lines, pass id and we fetch the invoice to pre-fill. */
  reissueFromId?: string;
  /** Editing an existing DRAFT in place (not creating a new comprobante). */
  draftId?: string;
}

export function NewInvoicePage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const state = location.state as LocationStateWithClient | null;
  const stateClient = state?.client;
  const reissueFrom = state?.reissueFrom;
  /**
   * reissueFromId: URL query param is the source of truth so a hard
   * reload, a deep-linked share, or an opened-in-new-tab "Re-emitir"
   * all keep the reissue context. We still honour location.state as a
   * fallback because previous in-app navigations only set state — this
   * keeps any inflight session working without forcing a single
   * caller path. */
  const reissueFromId =
    searchParams.get("reissueFromId") ?? state?.reissueFromId;
  /**
   * draftId: EDIT an existing draft in place rather than create a new
   * comprobante. Query param first for the same reason as reissueFromId — a
   * reload or an opened-in-new-tab "Editar" must not silently turn into a
   * blank new invoice. (It used to be passed only via router state, and
   * nothing here read it, so "Editar" always landed on an empty form.)
   */
  const editDraftId = searchParams.get("draftId") ?? state?.draftId;
  const isEditingDraft = Boolean(editDraftId);

  const [clientLocation, setClientLocation] = useState<ClientLocation>("uruguay");
  const [needRut, setNeedRut] = useState<boolean>(false);
  const [receiver, setReceiver] = useState<ReceiverForm>({
    name: "",
    rut: "",
    address: "",
    countryCode: "UY",
    docRecepExt: "",
    ciudad: "",
    departamento: "",
  });
  const [issueDate, setIssueDate] = useState(() => todayInUruguay());
  const [paymentMethod, setPaymentMethod] = useState<"contado" | "credito">("contado");
  /** This invoice's own adenda (CFE Zona J), appended to the standing one. */
  const [adendaText, setAdendaText] = useState("");
  /** Issue this invoice WITHOUT the marca's / company's standing adenda. */
  const [adendaSuppressDefault, setAdendaSuppressDefault] = useState(false);
  /**
   * This invoice's own version of the standing adenda; null = use it as it is.
   *
   * FORM STATE ONLY — there is no column for it, and none is needed. At submit
   * it becomes `adendaText = override + the extra note` with
   * `adendaSuppressDefault = true`, which is exactly what
   * resolveInvoiceAdendaFromSources already composes. Kept separate until then
   * because `adendaText` may already hold the user's own note, and writing the
   * override into it would delete what they typed.
   */
  const [standingOverride, setStandingOverride] = useState<string | null>(null);
  /**
   * The Adenda card is open. Closed by default when a fija exists — most
   * invoices need nothing from this section, because the fija already prints
   * on its own.
   *
   * It is NOT left closed when this invoice already has something of its own
   * to say. A draft reopened with its own note, or with an edited fija, must
   * show that text rather than hide it behind a disclosure the user has no
   * reason to open — so the seeding effect below opens it.
   *
   * THE SEEDING IS AN EFFECT, NOT A LAZY INITIALIZER. `() => reissueInvoice…`
   * reads fine and typechecks, and would throw `ReferenceError: Cannot access
   * 'reissueInvoice' before initialization` on every render: the query that
   * declares it runs ~160 lines below, and React calls the initializer
   * synchronously. tsc cannot see it because the read hides in a closure —
   * the same trap that made the client Productos tab a blank screen for two
   * months (bdf43bef).
   */
  const [adendaOpen, setAdendaOpen] = useState(false);
  /** The override editor is open (and holds its own draft until applied). */
  const [standingEditing, setStandingEditing] = useState(false);
  const [standingDraft, setStandingDraft] = useState("");
  const [standingSaving, setStandingSaving] = useState(false);

  // Marcas. The picker only exists for tenants that created one, so the form
  // is untouched for everyone else. Empty string = issue as the legal entity.
  const { data: brands = [] } = useQuery({
    queryKey: brandKeys.list(),
    queryFn: listBrands,
  });
  const [brandId, setBrandId] = useState<string>("");
  const brandTouched = useRef(false);
  const [linesForm, setLinesForm] = useState<LineForm[]>([
    {
      description: "",
      quantity: 1,
      unitPrice: 0,
      taxCategory: "vat_basic",
      taxAmount: 0,
    },
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [zeroConfirmOpen, setZeroConfirmOpen] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  /** Set while the zero-confirm dialog is open because handleScheduleSend (not
   * handleSubmit) triggered it — tells the dialog's confirm button which flow
   * to resume with confirmedZero:true. */
  const [pendingScheduleSendAt, setPendingScheduleSendAt] = useState<string | null>(null);
  /** Per-invoice override of the client's auto-send-email default — mainly for
   * scheduled drafts, since a fire-and-forget send gives the user no later
   * chance to notice the client default didn't match what they meant here. */
  const [autoSendEmailChoice, setAutoSendEmailChoice] = useState(true);
  const [previewPdfLoading, setPreviewPdfLoading] = useState(false);
  const [resultId, setResultId] = useState<string | null>(null);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [addClientOpen, setAddClientOpen] = useState(false);
  const [prefillApplied, setPrefillApplied] = useState(false);
  const reissuePrefillAppliedRef = useRef(false);
  const reissueClientSelectAppliedRef = useRef(false);
  const reissueAddressFetchAppliedRef = useRef(false);
  /** Dedupes auto-apply when catalog has exactly one item (per client + item). */
  const autoSingleCatalogKeyRef = useRef<string | null>(null);
  const [rucInfoLoading, setRucInfoLoading] = useState(false);
  const [lineCurrency, setLineCurrency] = useState<"UYU" | "USD">("UYU");
  const [exchangeRate, setExchangeRate] = useState(1);
  const [exchangeRateManual, setExchangeRateManual] = useState<string>("");

  const { data: clientsData, isLoading: clientsLoading } = useQuery({
    queryKey: clientKeys.list({ page: 1, limit: 50 }),
    queryFn: () => listClients({ page: 1, limit: 50 }),
  });
  const clients: Client[] = clientsData?.data ?? [];

  // Which CFE types this tenant can actually issue (from its active CAEs).
  // Gates the receiver-type options so a tenant can't pick a type it has no
  // CAE for (e.g. e-Ticket / foreign client without a 101 CAE → guaranteed
  // DGI failure). canEmitETicket also covers foreign clients (foreign → 101).
  const {
    canEmitETicket,
    canEmitEFactura,
    canEmitExport,
    isLoading: caesLoading,
  } = useIssuableCfeTypes();

  const { data: clientCatalogItems = [] } = useQuery({
    queryKey: clientKeys.catalog(selectedClientId ?? ""),
    queryFn: () => listClientCatalogItems(selectedClientId!),
    enabled: Boolean(selectedClientId),
  });

  /**
   * Company products load unconditionally — no client gate. That is the whole
   * point: the client catalog only ever appeared once a client was selected,
   * leaving the most common invoice (a consumidor-final e-Ticket with no
   * client) with nothing but a free-text description.
   */
  const { data: companyProducts = [] } = useQuery({
    queryKey: productKeys.list(),
    queryFn: listProducts,
    staleTime: 60_000,
  });

  /**
   * Client items FIRST: a price agreed with this client is the more specific
   * answer, so it wins position. Products stay reachable below rather than
   * being hidden — a tenant with 40 products and one client-specific price
   * should not see a one-item list on that client's invoice.
   */
  const pickerItems: CatalogPickerItem[] = useMemo(
    () => [
      ...clientCatalogItems.map((i) => ({ ...i, origin: "client" as const })),
      ...companyProducts.map((p) => ({
        ...p,
        // The picker types on ClientCatalogItem; a product has no clientId.
        clientId: "",
        origin: "product" as const,
      })),
    ],
    [clientCatalogItems, companyProducts],
  );

  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data: tenant } = useQuery({
    queryKey: tenantKeys.current(user?.tenantId ?? undefined),
    queryFn: getTenantCurrent,
    enabled: Boolean(user?.tenantId),
  });
  /**
   * LOCK the rate to Exento only for tenants who genuinely cannot charge IVA.
   *
   * That means Literal E: they sit in the IVA mínimo regime and invoice at 0%
   * by law. A selector defaulting to 22% once nearly put 22% IVA on a Literal
   * E client's CFE, which is why the lock exists.
   *
   * It used to key off `exoneratedRegimeEnabled` — but that flag only says
   * "put an exoneration leyenda on the invoice", and its legend dropdown mixes
   * two DIFFERENT taxes: IVA exonerations (exportación de servicios, Art. 19
   * Título 10, Literal E) alongside IRAE-only ones ("Servicios exonerados de
   * IRAE – Art. 52 Título 4", offered in every industry category). IRAE is
   * income tax and says nothing about IVA, so keying the IVA lock off it left
   * SALKANTAI SAS — ivaRegime 'no_cede', 94 previously taxed lines — unable to
   * charge IVA at all.
   */
  const forceExemptTax =
    tenant?.ivaRegime === "minimum" || tenant?.literalEVerified === true;

  /**
   * DEFAULT a new line to Exento — without disabling the selector — for an
   * exonerated tenant who is not Literal E. The original complaint was a
   * selector *defaulting* to 22%, so defaulting to Exento keeps that
   * protection while leaving the accountant free to apply IVA when the
   * exoneration is an IRAE one.
   */
  const defaultExemptTax =
    Boolean(tenant?.exoneratedRegimeEnabled) && !forceExemptTax;
  const { blocked: invoicingBlocked, reason: invoicingBlockedReason } =
    useInvoiceCreationBlocked();

  useEffect(() => {
    if (!tenant || !invoicingBlocked) return;
    navigate("/dashboard", { replace: true });
    const key = invoiceCreationBlockedEmptyExplanationKey(invoicingBlockedReason);
    if (key) toast(t(key), { id: "invoicing-blocked" });
  }, [tenant, invoicingBlocked, invoicingBlockedReason, navigate, t]);

  /**
   * One fetch feeds both prefill paths: re-issuing/replicating a source
   * comprobante, and editing a draft in place. The form hydration below is
   * identical for both — what differs is the issue date rule and what submit
   * does with the result.
   */
  const prefillFromId = reissueFromId ?? editDraftId;
  const { data: reissueInvoice, isLoading: reissueLoading } = useQuery({
    queryKey: invoiceKeys.detail(prefillFromId ?? ""),
    queryFn: () => getInvoice(prefillFromId!),
    enabled: Boolean(prefillFromId),
  });

  // All USD invoices (domestic 111 and foreign/export 101 alike) carry a CFE
  // TpoCambio that the backend band-checks against the BCU rate, so the form
  // must prefill the rate for every USD invoice — not just domestic ones.
  const exchangeRateDate = issueDate?.slice(0, 10) || undefined;
  const { data: exchangeRatesData } = useQuery({
    queryKey: dashboardKeys.exchangeRates(exchangeRateDate),
    queryFn: () => getExchangeRates(exchangeRateDate),
    enabled: lineCurrency === "USD",
  });
  useEffect(() => {
    if (!exchangeRatesData?.rates?.length || lineCurrency !== "USD") return;
    const usd = exchangeRatesData.rates.find((r) => r.currency === "USD");
    if (usd?.sell != null) {
      setExchangeRate(usd.sell);
      setExchangeRateManual("");
    }
  }, [exchangeRatesData, lineCurrency]);

  const effectiveExchangeRate =
    exchangeRateManual.trim() !== "" &&
    !Number.isNaN(Number(exchangeRateManual))
      ? Number(exchangeRateManual)
      : exchangeRate;

  const clientsForSelect = useMemo(() => {
    if (!stateClient) return clients;
    if (clients.some((c) => c.id === stateClient.id)) return clients;
    return [stateClient, ...clients];
  }, [clients, stateClient]);

  useEffect(() => {
    if (prefillApplied || !stateClient) return;
    setPrefillApplied(true);
    const client = stateClient;
    const docType = client.documentType ?? "RUT";
    setReceiver({
      name: client.legalName ?? "",
      rut: docType === "EXT" ? "" : (client.rut ?? ""),
      address: client.address ?? "",
      countryCode: client.countryCode ?? "UY",
      docRecepExt: client.docRecepExt ?? "",
      ciudad: client.ciudad ?? "",
      departamento: client.departamento ?? "",
    });
    setSelectedClientId(client.id);
    if (docType === "EXT") {
      setClientLocation("export");
      setNeedRut(false);
    } else if (docType === "CI") {
      setClientLocation("uruguay");
      setNeedRut(false);
    } else {
      setClientLocation("uruguay");
      setNeedRut(true);
    }
    const nextCfe = getCfeType(
      docType === "EXT" ? "export" : "uruguay",
      docType === "RUT"
    );
    const lineCur = lineCurrencyFromClientDefault(client, nextCfe);
    if (lineCur) setLineCurrency(lineCur);
    setPaymentMethod(paymentMethodFromPaymentTermDays(client.paymentTermDays));
  }, [stateClient, prefillApplied]);

  useEffect(() => {
    // Seed once, then never fight the user — a refetch must not silently
    // re-point a marca they chose.
    if (brandTouched.current || brands.length === 0) return;
    // Editing a draft or re-issuing keeps THAT document's marca. Falling back
    // to the tenant default here would quietly re-brand a comprobante the
    // client has already seen. Wait for the fetch rather than seeding early.
    if (prefillFromId) {
      if (!reissueInvoice) return;
      const existing = reissueInvoice.brandId ?? "";
      setBrandId(brands.some((b) => b.id === existing) ? existing : "");
      brandTouched.current = true;
      return;
    }
    setBrandId(brands.find((b) => b.isDefault)?.id ?? "");
    brandTouched.current = true;
  }, [brands, prefillFromId, reissueInvoice]);

  /**
   * The standing adenda this invoice would carry: the selected marca's, else
   * the company's. Mirrors resolveInvoiceAdendaFromSources on the backend —
   * `??`, never `||`, because a marca whose adenda is an EMPTY STRING has
   * deliberately cleared it and must not fall back to the company's.
   */
  const effectiveStandingAdenda = useMemo((): string => {
    const brand = brandId ? brands.find((b) => b.id === brandId) : undefined;
    return (brand?.adendaText ?? tenant?.adendaText ?? "").trim();
  }, [brandId, brands, tenant?.adendaText]);

  // Seed the adenda when editing a draft or re-issuing. Without this a draft
  // that already carries an adenda opened with an empty field and re-submitted
  // as though the user had deliberately cleared it.
  useEffect(() => {
    if (!reissueInvoice) return;
    // A stored suppress+text row cannot be split back into "edited the fija"
    // and "suppressed it and wrote something else" — both produce the same
    // CFE. Read it as an override, which is the more useful affordance: the
    // user gets the text in an editor with Restaurar la fija beside it.
    // Anything this invoice carries of its own opens the card — see the note
    // on adendaOpen for why this lives here and not in the initializer.
    if (
      reissueInvoice.adendaText?.trim() ||
      reissueInvoice.adendaSuppressDefault === true
    ) {
      setAdendaOpen(true);
    }
    if (reissueInvoice.adendaSuppressDefault === true && reissueInvoice.adendaText) {
      setStandingOverride(reissueInvoice.adendaText);
      setAdendaText("");
      setAdendaSuppressDefault(true);
      return;
    }
    setAdendaText(reissueInvoice.adendaText ?? "");
    setAdendaSuppressDefault(reissueInvoice.adendaSuppressDefault === true);
  }, [reissueInvoice]);

  // Drop a suppress flag whose checkbox is no longer on screen. Switching to a
  // marca with no standing adenda unmounts the control while the flag stayed
  // true, so it silently re-applied on switching back.
  /**
   * An override belongs to the marca it was taken against. Switching marca has
   * to drop it, and the earlier version did not: it only cleared when the new
   * standing adenda was EMPTY, so going from marca A (whose text the user had
   * edited) to marca B printed A's legend on an invoice issued under B — and
   * suppressed B's own, because an override forces adendaSuppressDefault.
   *
   * Keyed on brandId, NOT on the resolved text: the text arrives
   * asynchronously, so clearing on its first non-empty value would wipe an
   * override seeded from a saved draft before the user ever saw it.
   */
  const overrideBrandRef = useRef(brandId);
  useEffect(() => {
    if (overrideBrandRef.current === brandId) return;
    overrideBrandRef.current = brandId;
    setStandingOverride(null);
    setStandingEditing(false);
  }, [brandId]);

  useEffect(() => {
    if (!effectiveStandingAdenda) {
      setAdendaSuppressDefault(false);
      // Same reasoning for the override, and it matters more: an override that
      // survived a marca switch would print the OLD marca's text on an invoice
      // issued under a marca that never had it.
      setStandingOverride(null);
      setStandingEditing(false);
    }
  }, [effectiveStandingAdenda]);

  /**
   * What the invoice actually carries. An override is expressed in the two
   * fields the backend already composes — the standing text suppressed, and
   * this invoice's adenda holding the edited copy FOLLOWED BY the user's own
   * note. Order matches resolveInvoiceAdendaFromSources: Res. DGI 1264/023
   * numeral 3 puts mandatory legends before anything optional, and the
   * standing text is where a tenant's required legend lives.
   */
  /**
   * WHERE a persistent save lands, which is not always the company.
   *
   * `effectiveStandingAdenda` is the marca's text if the marca has one, else
   * the company's. So when a marca supplies it, the save must target THAT
   * MARCA: writing the company's adenda while the marca overrides it changes
   * a value nothing displays, and the user watches their edit evaporate.
   */
  const standingAdendaTarget = useMemo((): {
    kind: "brand" | "company";
    brandId?: string;
    label: string;
  } => {
    const brand = brandId ? brands.find((b) => b.id === brandId) : undefined;
    if (brand && brand.adendaText != null && brand.adendaText.trim() !== "") {
      return { kind: "brand", brandId: brand.id, label: brand.name };
    }
    return { kind: "company", label: "" };
  }, [brandId, brands]);

  /**
   * THE TWO TARGETS ARE GUARDED DIFFERENTLY, and gating both on the stricter
   * one invents a restriction the backend does not have.
   *
   * PATCH /tenants/current/adenda is behind TenantAdminOrRootGuard, so the
   * COMPANY adenda is admin-only. PATCH /brands/:brandId is behind AuthGuard
   * alone (brands.controller.ts:36), so any member may save a MARCA's. Hidden
   * rather than disabled where it does apply: a 403 after composing the text is
   * the worst moment to learn you cannot save.
   */
  const isTenantAdmin = useMemo((): boolean => {
    const role = user?.role ?? "";
    return ["root", "root_admin", "owner", "admin", "accountant"].includes(role);
  }, [user?.role]);
  const canEditStandingAdenda =
    standingAdendaTarget.kind === "brand" || isTenantAdmin;

  const standingAdendaSaveLabel =
    standingAdendaTarget.kind === "brand"
      ? t("invoices.new.adendaStandingSaveBrand", {
          defaultValue: "Guardar como fija de {{name}}",
          name: standingAdendaTarget.label,
        })
      : t("invoices.new.adendaStandingSaveCompany", "Guardar como adenda fija");

  const saveStandingAdenda = useCallback(async (): Promise<void> => {
    const next = standingDraft.trim();
    setStandingSaving(true);
    try {
      if (standingAdendaTarget.kind === "brand" && standingAdendaTarget.brandId) {
        await updateBrand(standingAdendaTarget.brandId, { adendaText: next });
      } else {
        await updateTenantAdenda(next === "" ? null : next);
      }
      // Both sources feed effectiveStandingAdenda through these queries, so the
      // box re-renders from the stored value rather than from local state.
      await queryClient.invalidateQueries({ queryKey: brandKeys.all });
      await queryClient.invalidateQueries({
        queryKey: tenantKeys.current(user?.tenantId ?? undefined),
      });
      // The edit IS the standing text now, so there is nothing to override.
      setStandingOverride(null);
      setStandingEditing(false);
      toast.success(
        standingAdendaTarget.kind === "brand"
          ? t("invoices.new.adendaStandingSavedBrand", {
              defaultValue: "Adenda fija de {{name}} actualizada",
              name: standingAdendaTarget.label,
            })
          : t("invoices.new.adendaStandingSavedCompany", "Adenda fija actualizada")
      );
    } catch {
      toast.error(
        t("invoices.new.adendaStandingSaveFailed", "No se pudo guardar la adenda fija.")
      );
    } finally {
      setStandingSaving(false);
    }
  }, [standingDraft, standingAdendaTarget, queryClient, user?.tenantId, t]);

  /**
   * The adenda fija box: the stored text, an Editar action, and — once an
   * override is taken — the "edited for this invoice" state with Restaurar.
   *
   * A function rather than inline JSX because the card now has two shapes
   * (collapsed when a fija exists, bare field when it does not) and this box
   * belongs to only one of them. Returns null when the fija is suppressed:
   * there is nothing to show, and the checkbox beside it says why.
   */
  const renderAdendaStandingBox = (): JSX.Element | null => {
    if (adendaSuppressDefault) return null;
    return (
      <div className="rounded-md border border-[var(--fx-ink-100)] bg-[var(--fx-paper-2)] p-3 dark:border-[var(--dk-border)] dark:bg-[var(--dk-surface2)]">
        <div className="flex items-start justify-between gap-2">
          <p className="text-xs font-medium text-muted-foreground">
            {standingOverride === null
              ? t("invoices.new.adendaStandingTitle", "Adenda fija")
              : t(
                  "invoices.new.adendaStandingEditedTitle",
                  "Adenda editada para esta factura"
                )}
          </p>
          {!standingEditing && (
            <div className="flex shrink-0 gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-auto px-2 py-1 text-xs"
                onClick={() => {
                  setStandingDraft(
                    standingOverride ?? effectiveStandingAdenda
                  );
                  setStandingEditing(true);
                }}
              >
                {t("invoices.new.adendaStandingEdit", "Editar")}
              </Button>
              {standingOverride !== null && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-auto px-2 py-1 text-xs"
                  onClick={() => setStandingOverride(null)}
                >
                  {t(
                    "invoices.new.adendaStandingRestore",
                    "Restaurar la fija"
                  )}
                </Button>
              )}
            </div>
          )}
        </div>

        {standingEditing ? (
          <div className="mt-2 space-y-2">
            <AdendaField
              id="invoice-standing-override"
              value={standingDraft}
              onChange={setStandingDraft}
              rows={4}
              disabled={standingSaving}
              // The surrounding box already names the field, and
              // the scope is stated on the buttons below.
              helper=""
            />
            <div className="flex flex-wrap gap-2">
              {/* Primary and local: changes nothing outside this
                  invoice, and Restaurar la fija undoes it. */}
              <Button
                type="button"
                size="sm"
                disabled={standingSaving}
                onClick={() => {
                  setStandingOverride(standingDraft);
                  setStandingEditing(false);
                }}
              >
                {t(
                  "invoices.new.adendaStandingUseOnce",
                  "Usar solo en esta factura"
                )}
              </Button>
              {/* Hidden, not disabled, for anyone the backend
                  guard would refuse — see canEditStandingAdenda. */}
              {canEditStandingAdenda && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={standingSaving}
                  onClick={() => void saveStandingAdenda()}
                >
                  {standingSaving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("common.saving", "Guardando...")}
                    </>
                  ) : (
                    standingAdendaSaveLabel
                  )}
                </Button>
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={standingSaving}
                onClick={() => setStandingEditing(false)}
              >
                {t("common.cancel", "Cancelar")}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {t(
                "invoices.new.adendaStandingRetroactivity",
                "Guardarla como fija solo afecta a las facturas que emitas a partir de ahora."
              )}
            </p>
          </div>
        ) : (
          /* Shown verbatim so "no incluir" is a decision about
             text the user can actually read, not a guess. */
          <p className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground">
            {standingOverride ?? effectiveStandingAdenda}
          </p>
        )}
      </div>
    );
  };

  /**
   * NEVER HIDE TEXT THE USER HAS ALREADY TYPED. The card only collapses when a
   * fija exists, and that can become true AFTER they started typing — they
   * pick a marca that has one, or the tenant query resolves late. The bare
   * field then becomes a collapsed card and their note vanishes behind a
   * disclosure they never opened, while still sitting in the payload.
   */
  useEffect(() => {
    if (!effectiveStandingAdenda) return;
    if (
      adendaText.trim() ||
      standingOverride !== null ||
      adendaSuppressDefault
    ) {
      setAdendaOpen(true);
    }
  }, [
    effectiveStandingAdenda,
    adendaText,
    standingOverride,
    adendaSuppressDefault,
  ]);

  /**
   * What the closed card says is happening: the STATE when there is one to
   * name (suppressed, edited), and otherwise the fija's first line — a
   * preview is the honest default when nothing special is going on. It also
   * mentions this invoice's own note, without which a closed card over an
   * invoice carrying one looks identical to a closed card over an invoice
   * carrying none.
   */
  const adendaSummary = useMemo((): string => {
    const parts: string[] = [];
    if (adendaSuppressDefault) {
      parts.push(
        t("invoices.new.adendaSummarySuppressed", "No se incluye la adenda fija")
      );
    } else if (standingOverride !== null) {
      parts.push(
        t("invoices.new.adendaSummaryEdited", "Editada para esta factura")
      );
    } else {
      parts.push(effectiveStandingAdenda.split("\n")[0]);
    }
    if (adendaText.trim()) {
      parts.push(
        t("invoices.new.adendaSummaryOwnNote", "+ texto de esta factura")
      );
    }
    return parts.join(" · ");
  }, [
    adendaSuppressDefault,
    standingOverride,
    effectiveStandingAdenda,
    adendaText,
    t,
  ]);

  const adendaPayload = useMemo(
    () => composeAdendaPayload(standingOverride, adendaText, adendaSuppressDefault),
    [standingOverride, adendaText, adendaSuppressDefault]
  );

  const reissuePayload = reissueFrom ?? (reissueInvoice
    ? {
        cfeType: reissueInvoice.cfeType as "101" | "111" | "121",
        receiverSnapshot: reissueInvoice.receiverSnapshot ?? {},
        lines: reissueInvoice.lines ?? [],
        currency: (reissueInvoice.currency ?? "UYU") as "UYU" | "USD",
        paymentMethod: (reissueInvoice.paymentMethod ?? "contado") as "contado" | "credito",
        customerId: reissueInvoice.customerId,
        exchangeRate: reissueInvoice.exchangeRate as number | undefined,
        issueDate: reissueInvoice.issueDate,
      }
    : null);

  useEffect(() => {
    if (!reissuePayload || reissuePrefillAppliedRef.current) return;
    reissuePrefillAppliedRef.current = true;
    const { cfeType, receiverSnapshot, lines, currency } = reissuePayload;
    if (cfeType === "101") {
      setClientLocation("uruguay");
      setNeedRut(false);
    } else if (cfeType === "111") {
      setClientLocation("uruguay");
      setNeedRut(true);
    } else {
      setClientLocation("export");
      setNeedRut(false);
    }
    // Anonymous e-Tickets persist the DGI placeholders "Consumidor
    // final" + "000000000000" into the receiverSnapshot at submit time
    // (backend invoice-from-dto.builder), so a naive prefill would
    // surface those system values in the form's "Nombre" and "RUT/C.I."
    // inputs — making the fields look like the user already typed
    // them. Strip the placeholders so the inputs reissue empty,
    // matching what the user originally entered (which was nothing).
    const rawName = receiverSnapshot?.name ?? "";
    const rawRut = receiverSnapshot?.rut ?? "";
    setReceiver({
      name: rawName === CONSUMER_PLACEHOLDER_NAME ? "" : rawName,
      rut: rawRut === CONSUMER_PLACEHOLDER_RUT ? "" : rawRut,
      address: receiverSnapshot?.address ?? "",
      countryCode: receiverSnapshot?.countryCode ?? "UY",
      docRecepExt: receiverSnapshot?.docRecepExt ?? "",
      ciudad: receiverSnapshot?.ciudad ?? "",
      departamento: receiverSnapshot?.departamento ?? "",
    });
    if ("paymentMethod" in reissuePayload && (reissuePayload.paymentMethod === "contado" || reissuePayload.paymentMethod === "credito")) {
      setPaymentMethod(reissuePayload.paymentMethod);
    }
    const mappedLines: LineForm[] =
      lines.length > 0
        ? lines.map((l) => ({
            description: l.description ?? "",
            quantity: l.quantity ?? 1,
            unitPrice: l.unitPrice?.amount ?? 0,
            taxCategory: (l.tax?.category ?? "vat_basic") as LineForm["taxCategory"],
            // Carried so a reopened draft / reissue restores the exact option
            // it was written with — vat_other alone cannot distinguish the
            // 2,2% automotor rate from the 2,75% moto one.
            taxRate: l.tax?.rate,
            taxAmount: l.tax?.amount ?? 0,
            dscItem: l.dscItem ?? "",
          }))
        : [
            {
              description: "",
              quantity: 1,
              unitPrice: 0,
              taxCategory: "vat_basic",
              taxAmount: 0,
            },
          ];
    // If we already know the tenant is on the exonerated regime by the
    // time the prefill runs (reissueInvoice resolved AFTER tenant), roll
    // the IVA back into unitPrice so the financial total survives the
    // forced exempt category. If reissueInvoice arrives first the
    // forceExemptTax effect below applies the same transform once
    // tenant lands — both orderings end up with the same line shape.
    const linesForState = forceExemptTax
      ? mappedLines.map(normalizeLineForExempt)
      : mappedLines;
    setLinesForm(linesForState);
    setLineCurrency(currency);
    // Reissue: honour the source CFE's issueDate so the rejected
    // transaction lands in the same fiscal period it originally
    // belonged to (the accountant's mental model is "fix the failure,
    // not move the sale"). Fall through to today only if the source
    // didn't carry a date or the date is somehow in the future — DGI
    // rejects TmstFirma > now+1d. Today is also the right default for
    // fresh non-reissue navigations, handled by the useState initial
    // value above.
    const sourceDate = reissuePayload.issueDate?.slice(0, 10);
    const todayStr = todayInUruguay();
    // Only a re-issue of a FAILED/REJECTED CFE keeps the source date — the
    // accountant's model is "fix the failure, don't move the sale". A plain
    // "Replicate" of an accepted invoice (same reissueFromId nav, but the
    // source was not rejected) is a brand-new sale → today's date.
    // dgiStatus is present at runtime but not on the declared type (same cast
    // the detail page uses). Rejected => genuine re-issue; otherwise replicate.
    const sourceDgiStatus = (
      reissueInvoice as unknown as Record<string, unknown> | undefined
    )?.dgiStatus as string | undefined;
    const isReissueOfRejected =
      Boolean(reissueFrom) || sourceDgiStatus === "rejected";
    setIssueDate(
      resolveIssueDateForPrefill({
        isEditingDraft,
        isReissueOfRejected,
        sourceDate,
        today: todayStr,
      }),
    );
  }, [reissuePayload, reissueFrom, reissueInvoice, isEditingDraft]);

  // On re-issue of an e-Factura, the source invoice may have been rejected by
  // DGI for missing DirRecep (A64) — meaning its address was empty. The form
  // pre-fill copies that empty address through. Auto-fetch from DGI here so
  // the next submission has the address DGI requires, without forcing the
  // user to blur the RUT field manually.
  useEffect(() => {
    if (!reissuePayload || reissueAddressFetchAppliedRef.current) return;
    if (reissuePayload.cfeType !== "111") return;
    const rut = (reissuePayload.receiverSnapshot?.rut ?? "").replace(/\D/g, "");
    if (rut.length !== 12) return;
    if (reissuePayload.receiverSnapshot?.address?.trim()) return;
    reissueAddressFetchAppliedRef.current = true;
    const normalized = rut.padStart(12, "0").slice(-12);
    setRucInfoLoading(true);
    getRucInfoForClient(normalized)
      .then((res) => {
        if (!res.valid) return;
        setReceiver((prev) => ({
          ...prev,
          ...(res.legalAddress?.trim() && !prev.address?.trim()
            ? { address: res.legalAddress.trim() }
            : {}),
          ...(res.city?.trim() && !prev.ciudad?.trim()
            ? { ciudad: res.city.trim() }
            : {}),
          ...(res.department?.trim() && !prev.departamento?.trim()
            ? { departamento: res.department.trim() }
            : {}),
        }));
      })
      .catch(() => {
        // Silent — backend will also try to enrich, and DGI will surface a
        // clear A64 rejection if the address is still missing at submit time.
      })
      .finally(() => {
        setRucInfoLoading(false);
      });
  }, [reissuePayload]);

  useEffect(() => {
    if (
      !reissuePayload ||
      clients.length === 0 ||
      reissueClientSelectAppliedRef.current
    )
      return;
    // Match by customerId first, then by RUT
    let client: Client | undefined;
    if (reissuePayload.customerId) {
      client = clients.find((c) => c.id === reissuePayload.customerId);
    }
    if (!client && reissuePayload.receiverSnapshot?.rut) {
      const rut = normalizeRut(reissuePayload.receiverSnapshot.rut);
      if (rut) {
        client = clients.find((c) => c.rut && normalizeRut(c.rut) === rut);
      }
    }
    if (client) {
      reissueClientSelectAppliedRef.current = true;
      setSelectedClientId(client.id);
      // Re-hydrate `receiver` from the client — not just the snapshot.
      // buildPayload (invoice-create-payload.ts) anonymizes rut on
      // every e-Ticket by writing CONSUMER_RUT = "" into the saved
      // receiverSnapshot, so the snapshot-only hydration above lands
      // with rut="" even when the original invoice was to a fully
      // identified client. The isEticketAnonymousOverThreshold gate
      // then mis-fires as if the receiver were Consumidor Final.
      // Re-pulling from the client (the authoritative source for
      // identification) keeps the threshold gate accurate when
      // replicating identified e-Tickets.
      const docType = client.documentType ?? "RUT";
      setReceiver({
        name: client.legalName ?? "",
        rut: docType === "EXT" ? "" : (client.rut ?? ""),
        address: client.address ?? "",
        countryCode: client.countryCode ?? "UY",
        docRecepExt: client.docRecepExt ?? "",
        ciudad: client.ciudad ?? "",
        departamento: client.departamento ?? "",
      });
    }
  }, [reissuePayload, clients]);

  const onSelectClient = useCallback(
    (clientId: string | null, clientOverride?: Client | null) => {
      setSelectedClientId(clientId);
      if (!clientId) {
        setReceiver({
          name: "",
          rut: "",
          address: "",
          countryCode: "",
          docRecepExt: "",
          ciudad: "",
          departamento: "",
        });
        setClientLocation("uruguay");
        setNeedRut(false);
        setLineCurrency("UYU");
        setPaymentMethod("contado");
        return;
      }
      const client =
        clientOverride ?? clientsForSelect.find((c) => c.id === clientId);
      if (!client) return;
      setAutoSendEmailChoice(client.autoSendInvoiceEmail !== false);
      const docType = client.documentType ?? "RUT";
      setReceiver({
        name: client.legalName ?? "",
        rut: docType === "EXT" ? "" : (client.rut ?? ""),
        address: client.address ?? "",
        countryCode: client.countryCode ?? "UY",
        docRecepExt: client.docRecepExt ?? "",
        ciudad: client.ciudad ?? "",
        departamento: client.departamento ?? "",
      });
      if (docType === "EXT") {
        setClientLocation("export");
        setNeedRut(false);
      } else if (docType === "CI") {
        setClientLocation("uruguay");
        setNeedRut(false);
      } else {
        setClientLocation("uruguay");
        setNeedRut(true);
      }
      const nextCfe = getCfeType(
        docType === "EXT" ? "export" : "uruguay",
        docType === "RUT"
      );
      const lineCur = lineCurrencyFromClientDefault(client, nextCfe);
      if (lineCur) setLineCurrency(lineCur);
      setPaymentMethod(paymentMethodFromPaymentTermDays(client.paymentTermDays));
    },
    [clientsForSelect]
  );

  // Receiver pre-selected by the Nueva-factura picker dialog
  // (/invoices/new?clientId=…). Applied once the client list has actually
  // loaded, because onSelectClient resolves via clientsForSelect.find and
  // returns early on a miss — running it against an empty list would silently
  // do nothing. The ref makes it run ONCE: without it, a background refetch of
  // the clients query would re-apply the param and clobber a receiver the user
  // had since edited by hand.
  const prefillClientId = searchParams.get("clientId");
  const appliedPrefillClientRef = useRef(false);
  useEffect(() => {
    if (!prefillClientId) return;
    if (appliedPrefillClientRef.current) return;
    if (clientsForSelect.length === 0) return;
    const match = clientsForSelect.find((c) => c.id === prefillClientId);
    if (!match) {
      // Client not in the list (deleted, or beyond the page the form fetches).
      // Mark as handled so we don't retry on every refetch and leave the form
      // on its default Consumidor-Final state.
      appliedPrefillClientRef.current = true;
      return;
    }
    appliedPrefillClientRef.current = true;
    onSelectClient(match.id, match);
  }, [prefillClientId, clientsForSelect, onSelectClient]);

  const cfeType = useMemo(
    () => getCfeType(clientLocation, needRut),
    [clientLocation, needRut]
  );

  const hasClientSelected = selectedClientId != null;
  const selectedClient = useMemo(
    () => clientsForSelect.find((c) => c.id === selectedClientId) ?? null,
    [clientsForSelect, selectedClientId]
  );

  /** Whether the tenant holds an active CAE for the CFE type this form would
   * emit. Foreign clients resolve to 101, so they're gated by the e-Ticket CAE.
   * While CAEs are still loading we treat it as issuable to avoid a flash. */
  const cfeTypeIssuable = useMemo(() => {
    if (caesLoading) return true;
    if (cfeType === "101") return canEmitETicket;
    if (cfeType === "111") return canEmitEFactura;
    if (cfeType === "121") return canEmitExport;
    return true;
  }, [caesLoading, cfeType, canEmitETicket, canEmitEFactura, canEmitExport]);

  // Default the manual Consumidor-Final/Empresa toggle to the type the tenant
  // can actually issue when only one is available (e.g. a tenant with only an
  // e-Factura CAE lands on "Empresa", not the un-issuable "Consumidor Final").
  useEffect(() => {
    if (caesLoading || hasClientSelected) return;
    if (cfeType === "101" && !canEmitETicket && canEmitEFactura) {
      setClientLocation("uruguay");
      setNeedRut(true);
    } else if (cfeType === "111" && !canEmitEFactura && canEmitETicket) {
      setClientLocation("uruguay");
      setNeedRut(false);
    }
  }, [
    caesLoading,
    hasClientSelected,
    cfeType,
    canEmitETicket,
    canEmitEFactura,
  ]);

  const fetchReceiverRucOnBlur = useCallback(() => {
    if (hasClientSelected || cfeType !== "111") return;
    const digits = (receiver.rut ?? "").replace(/\D/g, "");
    if (digits.length !== 12) return;
    const normalized = digits.padStart(12, "0").slice(-12);
    setRucInfoLoading(true);
    getRucInfoForClient(normalized)
      .then((res) => {
        if (!res.valid) {
          toast.error(
            t(
              "clients.form.rucInfoInvalid",
              "Could not load company data for this RUT."
            )
          );
          return;
        }
        setReceiver((prev) => ({
          ...prev,
          ...(res.legalName?.trim() ? { name: res.legalName.trim() } : {}),
          ...(res.legalAddress?.trim() ? { address: res.legalAddress.trim() } : {}),
          ...(res.city?.trim() ? { ciudad: res.city.trim() } : {}),
          ...(res.department?.trim() ? { departamento: res.department.trim() } : {}),
        }));
      })
      .catch(() => {
        toast.error(
          t(
            "clients.form.rucInfoError",
            "Could not load company data. Please try again."
          )
        );
      })
      .finally(() => {
        setRucInfoLoading(false);
      });
  }, [hasClientSelected, cfeType, receiver.rut, t]);

  /**
   * When the tenant turns out to be on the exonerated regime (the
   * tenant fetch is async, so initial form state may have used the
   * vat_basic default), snap every existing line to exempt — but
   * preserve the line's existing financial total by rolling any IVA
   * back into unitPrice via `normalizeLineForExempt`. This covers
   * three races against the lock:
   *   - default empty line on mount before tenant lands (no-op, the
   *     line is unitPrice=0/taxAmount=0 anyway)
   *   - reissue prefill that landed before tenant (lines carry the
   *     original IVA; we re-tile them)
   *   - admin flipping the regime mid-form-session (rare)
   */
  useEffect(() => {
    if (!forceExemptTax) return;
    setLinesForm((prev) => {
      let changed = false;
      const next = prev.map((line) => {
        if (line.taxCategory === "exempt") return line;
        changed = true;
        return normalizeLineForExempt(line);
      });
      return changed ? next : prev;
    });
  }, [forceExemptTax]);

  const addLine = useCallback(() => {
    setLinesForm((prev) => [
      ...prev,
      {
        description: "",
        quantity: 1,
        unitPrice: 0,
        // Locked OR merely defaulted — a blank line starts Exento either way;
        // the difference is whether the selector can be changed afterwards.
        taxCategory: forceExemptTax || defaultExemptTax ? "exempt" : "vat_basic",
        taxAmount: 0,
      },
    ]);
  }, [forceExemptTax, defaultExemptTax]);

  const updateLine = useCallback((index: number, patch: Partial<LineForm>) => {
    setLinesForm((prev) =>
      prev.map((line, i) => {
        if (i !== index) return line;
        const updated = { ...line, ...patch };
        const needsTaxRecalc =
          "quantity" in patch ||
          "unitPrice" in patch ||
          "taxCategory" in patch ||
          "taxRate" in patch;
        if (needsTaxRecalc) {
          // Rate-driven, NOT category-driven. getLineTaxAmount looks the rate
          // up by category, and vat_other maps to 0 there — so an automotora
          // line would have computed zero tax, which is the entire defect this
          // feature exists to fix.
          updated.taxAmount = getLineTaxAmountAtRate(
            updated.taxRate ?? TAX_RATE_BY_CATEGORY[updated.taxCategory] ?? 0,
            updated.quantity,
            updated.unitPrice
          );
        }
        return updated;
      })
    );
  }, []);

  const applyCatalogItemToLine = useCallback(
    (index: number, item: ClientCatalogItem) => {
      const tax: TaxCategory = forceExemptTax
        ? "exempt"
        : cfeType === "121"
          ? "export"
          : ((item.defaultTaxCategory as TaxCategory) || "vat_basic");
      updateLine(index, {
        description: item.description,
        unitPrice: item.unitPrice,
        taxCategory: tax,
        // The row's own rate when it is still offered to this tenant, else
        // the category's standard option. The re-validation matters: a tenant
        // whose régimen was REMOVED must not keep invoicing at 2,2% from
        // rows catalogued while they had it.
        taxRate: resolveCatalogItemRate(
          tax,
          item.defaultTaxRate,
          tenant?.specialRegime as SpecialRegime | null,
          TAX_RATE_BY_CATEGORY,
        ),
        catalogItemId: item.id,
        dscItem: "",
      });
    },
    [cfeType, forceExemptTax, updateLine]
  );

  const resetCatalogLineToManual = useCallback(
    (index: number) => {
      const defaultTax: TaxCategory =
        forceExemptTax || defaultExemptTax
          ? "exempt"
          : cfeType === "121"
            ? "export"
            : "vat_basic";
      updateLine(index, {
        description: "",
        quantity: 1,
        unitPrice: 0,
        taxCategory: defaultTax,
        catalogItemId: null,
        dscItem: "",
      });
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[`line_${index}_description`];
        delete next[`line_${index}_quantity`];
        delete next[`line_${index}_unitPrice`];
        delete next[`line_${index}_taxAmount`];
        return next;
      });
    },
    [cfeType, forceExemptTax, defaultExemptTax, updateLine]
  );

  useEffect(() => {
    if (reissueFromId || reissuePayload) {
      return;
    }
    if (!selectedClientId) {
      autoSingleCatalogKeyRef.current = null;
      return;
    }
    if (clientCatalogItems.length !== 1) {
      autoSingleCatalogKeyRef.current = null;
      return;
    }
    const item = clientCatalogItems[0];
    const key = `${selectedClientId}:${item.id}`;
    if (autoSingleCatalogKeyRef.current === key) {
      return;
    }
    autoSingleCatalogKeyRef.current = key;
    applyCatalogItemToLine(0, item);
  }, [
    selectedClientId,
    clientCatalogItems,
    applyCatalogItemToLine,
    reissueFromId,
    reissuePayload,
  ]);

  const removeLine = useCallback((index: number) => {
    setLinesForm((prev) =>
      prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)
    );
  }, []);

  const validateStep3 = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    if (!issueDate) {
      errors.issueDate = t("invoices.new.validation.issueDateRequired");
    } else if (!isEditingDraft && issueDate > tomorrowInUruguay()) {
      // DGI tolerates a CFE dated one day ahead but not further — confirmed
      // empirically 2026-08-28. Editing a draft is exempt: a recurring
      // schedule generates its draft ahead of the scheduled send date, and
      // that's not the field being submitted to DGI yet — see
      // resolveIssueDateForPrefill.
      errors.issueDate = t("invoices.new.validation.issueDateFuture");
    }
    if (cfeType === "111") {
      // Validate the RAW value: normalizeRut pads to 12 and so can never fail
      // a length check. See hasValidRutDigits.
      if (!hasValidRutDigits(receiver.rut)) {
        errors.receiverRut = t("invoices.new.validation.rutLength");
      }
      if (!receiver.name.trim()) {
        errors.receiverName = t("invoices.new.validation.nameRequired");
      }
    }
    if (cfeType === "121") {
      if (!receiver.name.trim()) {
        errors.receiverName = t("invoices.new.validation.nameRequired");
      }
      if (!receiver.countryCode || receiver.countryCode === "OTHER") {
        errors.countryCode = t("invoices.create.countryRequired");
      }
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [cfeType, receiver, issueDate, isEditingDraft, t]);

  const validateStep4 = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    linesForm.forEach((line, i) => {
      if (!line.description.trim()) {
        errors[`line_${i}_description`] = t(
          "invoices.new.validation.descriptionRequired"
        );
      }
      if (line.quantity <= 0) {
        errors[`line_${i}_quantity`] = t(
          "invoices.new.validation.quantityPositive"
        );
      }
      if (line.unitPrice < 0) {
        errors[`line_${i}_unitPrice`] = t(
          "invoices.new.validation.unitPriceNonNegative"
        );
      }
      if (line.taxAmount < 0) {
        errors[`line_${i}_taxAmount`] = t(
          "invoices.new.validation.taxAmountNonNegative"
        );
      }
    });
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [linesForm, t]);

  const handleSubmit = useCallback(async (opts?: { confirmedZero?: boolean }) => {
    if (!cfeType) return;
    if (
      (cfeType === "101" || cfeType === "111") &&
      lineCurrency === "USD" &&
      (effectiveExchangeRate < 0.01 || !Number.isFinite(effectiveExchangeRate))
    ) {
      toast.error(t("invoices.new.validation.exchangeRateRequired"));
      return;
    }
    // Kept as two short-circuited checks rather than one combined condition:
    // each validator calls setFieldErrors with ITS OWN error object, so
    // running step 4 after step 3 already failed would overwrite the step-3
    // errors and show the user the wrong ones.
    //
    // This friction never reaches the API, so it is invisible in backend logs
    // — a user stuck here looks identical to one who never tried to invoice.
    if (!validateStep3()) {
      trackValidationBlocked("new-invoice", "step3");
      return;
    }
    if (!validateStep4()) {
      trackValidationBlocked("new-invoice", "step4");
      return;
    }
    // A $0 comprobante is legal but emitted as Entrega Gratuita (free delivery) —
    // DGI forbids a priced line at 0. Confirm the user meant $0 before issuing, so an
    // accidental zero isn't silently documented as a giveaway. `totals` is declared
    // below, so compute the gross inline from linesForm (already a dependency).
    const grossIsZero =
      linesForm.reduce((s, l) => s + l.quantity * l.unitPrice + l.taxAmount, 0) === 0;
    if (grossIsZero && !opts?.confirmedZero) {
      setZeroConfirmOpen(true);
      return;
    }
    setSubmitting(true);
    try {
      const regimeLegend =
        tenant?.exoneratedRegimeEnabled && tenant?.exoneratedRegimeLegend?.trim()
          ? (tenant.exoneratedRegimeHeaderLegend?.trim() || tenant.exoneratedRegimeLegend)
          : undefined;
      const payload = buildPayload(
        cfeType,
        receiver,
        issueDate,
        linesForm,
        cfeType === "121" ? "USD" : lineCurrency,
        cfeType === "121" ? 1 : effectiveExchangeRate,
        regimeLegend,
        paymentMethod,
        adendaPayload
      );
      if (selectedClientId) payload.customerId = selectedClientId;
      if (brandId) payload.brandId = brandId;
      if (selectedClientId && selectedClient?.sendInvoiceByEmail) {
        payload.autoSendEmailOverride = autoSendEmailChoice;
      }
      // Editing a draft UPDATES it. sourceInvoiceId is deliberately not set:
      // that field links a replacement to the comprobante it replaces, and an
      // edited draft is still the same comprobante.
      if (editDraftId) {
        await updateDraftInvoice(editDraftId, payload);
        toast.success(t("invoices.create.draftUpdated", "Borrador actualizado"));
        navigate(`/invoices/${editDraftId}`, { replace: true });
        return;
      }
      if (reissueFromId) payload.sourceInvoiceId = reissueFromId;
      const created = await createInvoice(payload);
      toast.success(t("invoices.create.created"));
      navigate(`/invoices/${created.id}`, { replace: true });
    } catch (err) {
      const data =
        err &&
        typeof err === "object" &&
        "response" in err
          ? (err as { response?: { data?: unknown } }).response?.data
          : null;
      const message =
        data && typeof data === "object" && data !== null && "message" in data
          ? (data as { message?: string | string[] }).message
          : null;
      const msg = Array.isArray(message) ? message[0] : message;
      const fallback = t("invoices.new.error");
      const errorMsg =
        typeof msg === "string" && msg.trim() ? msg : fallback;
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  }, [
    cfeType,
    receiver,
    issueDate,
    linesForm,
    lineCurrency,
    effectiveExchangeRate,
    paymentMethod,
    tenant?.exoneratedRegimeEnabled,
    tenant?.exoneratedRegimeLegend,
    validateStep3,
    validateStep4,
    selectedClientId,
    selectedClient,
    autoSendEmailChoice,
    t,
  ]);

  const handleSaveDraft = useCallback(async () => {
    if (!cfeType) return;
    setSavingDraft(true);
    try {
      const regimeLegend =
        tenant?.exoneratedRegimeEnabled && tenant?.exoneratedRegimeLegend?.trim()
          ? (tenant.exoneratedRegimeHeaderLegend?.trim() || tenant.exoneratedRegimeLegend)
          : undefined;
      const payload = buildPayload(
        cfeType,
        receiver,
        issueDate,
        linesForm,
        cfeType === "121" ? "USD" : lineCurrency,
        cfeType === "121" ? 1 : effectiveExchangeRate,
        regimeLegend,
        paymentMethod,
        adendaPayload
      );
      if (selectedClientId) payload.customerId = selectedClientId;
      if (selectedClientId && selectedClient?.sendInvoiceByEmail) {
        payload.autoSendEmailOverride = autoSendEmailChoice;
      }
      const created = await createDraftInvoice(payload);
      toast.success(t("invoices.create.draftSaved", "Draft saved"));
      navigate(`/invoices/${created.id}`, { replace: true });
    } catch (err) {
      const data =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: unknown } }).response?.data
          : null;
      const message =
        data && typeof data === "object" && data !== null && "message" in data
          ? (data as { message?: string | string[] }).message
          : null;
      const msg = Array.isArray(message) ? message[0] : message;
      toast.error(typeof msg === "string" && msg.trim() ? msg : t("invoices.new.error"));
    } finally {
      setSavingDraft(false);
    }
  }, [
    cfeType,
    receiver,
    issueDate,
    linesForm,
    lineCurrency,
    effectiveExchangeRate,
    paymentMethod,
    tenant?.exoneratedRegimeEnabled,
    tenant?.exoneratedRegimeLegend,
    selectedClientId,
    t,
  ]);

  /**
   * Schedule send (Gmail-style): create the draft (or reuse the one being
   * edited), then schedule it. The draft's issueDate is left as-is here —
   * it's rewritten server-side to the actual Uruguay-local calendar date
   * at fire time, not at schedule time (see ScheduledInvoiceSendService),
   * so the form's own issueDate value doesn't need to match.
   */
  const handleScheduleSend = useCallback(
    async (sendAtIso: string, opts?: { confirmedZero?: boolean }) => {
      if (!cfeType) return;
      if (!validateStep3() || !validateStep4()) {
        // The errors are set on the underlying form, which the dialog is
        // covering — close it so the user actually sees what's wrong,
        // mirroring what handleSubmit shows immediately (no modal in front).
        setScheduleDialogOpen(false);
        return;
      }
      // Same $0 confirmation gate as handleSubmit — a scheduled draft would
      // otherwise auto-send to DGI without ever showing this prompt.
      const grossIsZero =
        linesForm.reduce((s, l) => s + l.quantity * l.unitPrice + l.taxAmount, 0) === 0;
      if (grossIsZero && !opts?.confirmedZero) {
        setPendingScheduleSendAt(sendAtIso);
        setScheduleDialogOpen(false);
        setZeroConfirmOpen(true);
        return;
      }
      setScheduling(true);
      try {
        const regimeLegend =
          tenant?.exoneratedRegimeEnabled && tenant?.exoneratedRegimeLegend?.trim()
            ? (tenant.exoneratedRegimeHeaderLegend?.trim() || tenant.exoneratedRegimeLegend)
            : undefined;
        const payload = buildPayload(
          cfeType,
          receiver,
          issueDate,
          linesForm,
          cfeType === "121" ? "USD" : lineCurrency,
          cfeType === "121" ? 1 : effectiveExchangeRate,
          regimeLegend,
          paymentMethod,
          adendaPayload
        );
        if (selectedClientId) payload.customerId = selectedClientId;
        if (selectedClientId && selectedClient?.sendInvoiceByEmail) {
          payload.autoSendEmailOverride = autoSendEmailChoice;
        }

        let draftId: string;
        let createdNewDraft = false;
        if (editDraftId) {
          draftId = (await updateDraftInvoice(editDraftId, payload)).id;
        } else {
          draftId = (await createDraftInvoice(payload)).id;
          createdNewDraft = true;
        }
        try {
          const scheduled = await scheduleInvoiceSend(draftId, sendAtIso);
          setScheduleDialogOpen(false);
          toast.success(t("invoices.schedule.scheduled", "Comprobante programado"));
          navigate(`/invoices/${scheduled.id}`, { replace: true });
        } catch (scheduleErr) {
          // The draft was created fine — only the schedule call failed (e.g.
          // the queue is down). Don't leave an orphaned duplicate draft
          // behind for every retry; a pre-existing edited draft is left
          // alone since the user didn't ask to create it just now.
          if (createdNewDraft) {
            await deleteDraftInvoice(draftId).catch(() => {});
          }
          throw scheduleErr;
        }
      } catch (err) {
        const data =
          err && typeof err === "object" && "response" in err
            ? (err as { response?: { data?: unknown } }).response?.data
            : null;
        const message =
          data && typeof data === "object" && data !== null && "message" in data
            ? (data as { message?: string | string[] }).message
            : null;
        const msg = Array.isArray(message) ? message[0] : message;
        toast.error(typeof msg === "string" && msg.trim() ? msg : t("invoices.new.error"));
      } finally {
        setScheduling(false);
      }
    },
    [
      cfeType,
      receiver,
      issueDate,
      linesForm,
      lineCurrency,
      effectiveExchangeRate,
      paymentMethod,
      tenant?.exoneratedRegimeEnabled,
      tenant?.exoneratedRegimeLegend,
      tenant?.exoneratedRegimeHeaderLegend,
      selectedClientId,
      selectedClient,
      autoSendEmailChoice,
      editDraftId,
      validateStep3,
      validateStep4,
      navigate,
      t,
    ]
  );

  const handlePreview = useCallback(async () => {
    if (!cfeType) return;
    if (
      (cfeType === "101" || cfeType === "111") &&
      lineCurrency === "USD" &&
      (effectiveExchangeRate < 0.01 || !Number.isFinite(effectiveExchangeRate))
    ) {
      toast.error(t("invoices.new.validation.exchangeRateRequired"));
      return;
    }
    if (!validateStep3() || !validateStep4()) return;
    setPreviewPdfLoading(true);
    try {
      const regimeLegend =
        tenant?.exoneratedRegimeEnabled && tenant?.exoneratedRegimeLegend?.trim()
          ? (tenant.exoneratedRegimeHeaderLegend?.trim() || tenant.exoneratedRegimeLegend)
          : undefined;
      const payload = buildPayload(
        cfeType,
        receiver,
        issueDate,
        linesForm,
        cfeType === "121" ? "USD" : lineCurrency,
        cfeType === "121" ? 1 : effectiveExchangeRate,
        regimeLegend,
        paymentMethod,
        adendaPayload
      );
      if (selectedClientId) payload.customerId = selectedClientId;
      await previewInvoicePdf(payload);
    } catch (err) {
      toast.error(t("invoices.create.downloadPdfError"));
    } finally {
      setPreviewPdfLoading(false);
    }
  }, [
    cfeType,
    receiver,
    issueDate,
    linesForm,
    lineCurrency,
    effectiveExchangeRate,
    paymentMethod,
    selectedClientId,
    tenant?.exoneratedRegimeEnabled,
    tenant?.exoneratedRegimeLegend,
    validateStep3,
    validateStep4,
    t,
  ]);

  /**
   * The rate for a line: the option's when the line carries one, otherwise the
   * old category lookup. Both computations below go through this so the totals
   * preview and the submitted payload cannot disagree.
   */
  const resolveLineRate = (line: LineForm): number =>
    line.taxRate ?? TAX_RATE_BY_CATEGORY[line.taxCategory] ?? 0;

  /** The rates this tenant may pick — the standard five, plus the régimen
   *  ones when their fiscal profile carries a régimen especial. */
  const availableTaxOptions = useMemo(
    () => taxOptionsForRegime(tenant?.specialRegime as SpecialRegime | null),
    [tenant?.specialRegime],
  );

  const displayCurrency = cfeType === "121" ? "USD" : lineCurrency;
  const totals = useMemo(() => {
    const subtotal = linesForm.reduce(
      (s, l) => s + l.quantity * l.unitPrice,
      0
    );
    const taxTotal = linesForm.reduce((s, l) => s + l.taxAmount, 0);
    return {
      subtotal,
      taxTotal,
      total: subtotal + taxTotal,
    };
  }, [linesForm]);

  /**
   * Live gross total in UYU. Used to detect when an anonymous e-Ticket
   * exceeds DGI's Tabla E threshold (currently UYU 10.000) — over that,
   * DGI requires receptor identification (TipoDocRecep + DocRecep), so we
   * gate the submit button until the user either fills in a RUT/C.I. or
   * switches to e-Factura.
   */
  const totalUyu = useMemo(() => {
    const gross = linesForm.reduce((sum, line) => {
      const rate = resolveLineRate(line);
      return sum + line.quantity * line.unitPrice * (1 + rate);
    }, 0);
    if (lineCurrency === "USD") {
      const rate = Number.isFinite(effectiveExchangeRate) ? effectiveExchangeRate : 0;
      return gross * rate;
    }
    return gross;
  }, [linesForm, lineCurrency, effectiveExchangeRate]);

  /**
   * True iff an anonymous Consumidor Final e-Ticket would exceed Tabla E.
   * Receptor is anonymous when no RUT/C.I. is typed (we treat the whole
   * 000000000000 placeholder as anonymous too — same as the backend).
   */
  /**
   * Threshold is 5,000 UI in pesos for the invoice's issueDate (UI value is
   * date-aware — see packages/shared-types/src/dgi-thresholds.ts).
   * `thresholdUyu` is also surfaced to the gate banner copy so the user sees
   * the actual UYU number the limit translates to today.
   */
  const thresholdUyu = useMemo(
    () => getEticketThresholdUyu(issueDate),
    [issueDate],
  );

  const isEticketAnonymousOverThreshold = useMemo(() => {
    if (cfeType !== "101") return false;
    // A selected client always identifies the receiver — even if the
    // receiver.rut field hasn't been re-hydrated yet (replicate hydration
    // can land after this memo recomputes, and the snapshot itself stores
    // an empty rut for e-Tickets per invoice-create-payload.ts:146-156).
    // Trust the dropdown's selection: if a client is picked, this is not
    // a Consumidor Final invoice and the threshold gate doesn't apply.
    if (selectedClientId != null) return false;
    const rutDigits = (receiver.rut ?? "").replace(/\D/g, "");
    const anonymous = !rutDigits || rutDigits === "000000000000";
    if (!anonymous) return false;
    return totalUyu > thresholdUyu;
  }, [cfeType, selectedClientId, receiver.rut, totalUyu, thresholdUyu]);

  const canCreateInvoice = useMemo((): boolean => {
    if (!cfeType) return false;
    if (!issueDate?.trim()) return false;
    if (cfeType === "111") {
      if (!hasValidRutDigits(receiver.rut)) return false;
      if (!receiver.name.trim()) return false;
    }
    if (cfeType === "121") {
      if (!receiver.name.trim()) return false;
      if (!receiver.countryCode || receiver.countryCode === "OTHER") return false;
    }
    if (
      (cfeType === "101" || cfeType === "111") &&
      lineCurrency === "USD" &&
      (effectiveExchangeRate < 0.01 || !Number.isFinite(effectiveExchangeRate))
    ) {
      return false;
    }
    if (isEticketAnonymousOverThreshold) return false;
    // No active CAE for the CFE type this invoice would emit (e.g. a foreign
    // client → 101 on a tenant with only an e-Factura CAE) → block with an
    // explanation instead of a guaranteed DGI rejection at emit time.
    if (!cfeTypeIssuable) return false;
    const hasValidLine = linesForm.some(
      (l) =>
        l.description.trim() !== "" &&
        l.quantity > 0 &&
        l.unitPrice >= 0 &&
        l.taxAmount >= 0
    );
    if (!hasValidLine) return false;
    const allLinesValid = linesForm.every(
      (l) =>
        l.description.trim() !== "" &&
        l.quantity > 0 &&
        l.unitPrice >= 0 &&
        l.taxAmount >= 0
    );
    return allLinesValid;
  }, [
    cfeType,
    issueDate,
    receiver,
    lineCurrency,
    effectiveExchangeRate,
    linesForm,
    isEticketAnonymousOverThreshold,
    cfeTypeIssuable,
  ]);

  if (prefillFromId && reissueLoading) {
    return <LoadingPage />;
  }

  if (tenant && invoicingBlocked) {
    return <LoadingPage />;
  }

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        title={
          isEditingDraft
            ? t("invoices.new.editDraftTitle", "Editar borrador")
            : t("invoices.new.title")
        }
        description={
          isEditingDraft
            ? t(
                "invoices.new.editDraftHelper",
                "Los cambios se guardan en este borrador. Se envía a DGI desde el detalle, cuando confirmes.",
              )
            : reissuePayload
              ? t("invoices.new.reissueHelper")
              : undefined
        }
        onBack={() => {
          // Browser-back preserves whatever filter / pagination / search
          // state the previous /invoices page had. Hardcoding navigate
          // ("/invoices") wiped that and the accountant had to re-apply
          // the same filters every reemit. Fall through to a stable
          // landing only when there's no history to pop (deep-link
          // case) so we don't get stuck on a blank navigation.
          if (window.history.length > 1) {
            navigate(-1);
          } else {
            navigate("/invoices");
          }
        }}
      />

      {/* Client selection */}
      {cfeType && (
        <Card>
          <CardHeader>
            <CardTitle>{t("invoices.new.receiver")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label
                htmlFor="selectClient"
                className="text-sm font-semibold text-foreground"
              >
                {t("invoices.create.selectClient", "Select a client")}
              </Label>
              <Select
                value={selectedClientId ?? "none"}
                onValueChange={(v) => {
                  if (v === CLIENT_SELECT_ADD_NEW_VALUE) {
                    setAddClientOpen(true);
                    return;
                  }
                  onSelectClient(v === "none" ? null : v);
                }}
                disabled={clientsLoading}
              >
                <SelectTrigger id="selectClient" className="h-10">
                  <SelectValue
                    placeholder={
                      clientsLoading
                        ? t("invoices.create.loadingClients", "Loading…")
                        : t("invoices.create.selectClientPlaceholder", "Optional – choose to pre-fill")
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    {t("invoices.create.noClient", "None")}
                  </SelectItem>
                  {clientsForSelect.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.displayName ?? c.legalName}
                      {c.rut ? ` (${c.rut})` : c.docRecepExt ? ` (${c.docRecepExt})` : ""}
                    </SelectItem>
                  ))}
                  <SelectItem
                    value={CLIENT_SELECT_ADD_NEW_VALUE}
                    className="cursor-pointer text-primary focus:text-primary"
                  >
                    {t("invoices.create.addNewClient")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {selectedClient?.sendInvoiceByEmail && (
              <div className="mt-4 flex items-start space-x-2 space-y-0 rounded-md border p-4 transition-colors duration-150">
                <Checkbox
                  id="autoSendEmailOverride"
                  checked={autoSendEmailChoice}
                  onCheckedChange={(checked) => {
                    setAutoSendEmailChoice(checked === true);
                  }}
                />
                <div className="grid gap-1.5 leading-none">
                  <Label
                    htmlFor="autoSendEmailOverride"
                    className="cursor-pointer text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {t("invoices.create.autoSendEmailOverride", "Enviar por email automáticamente al confirmarse")}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {autoSendEmailChoice === (selectedClient.autoSendInvoiceEmail !== false)
                      ? t(
                          "invoices.create.autoSendEmailOverrideHelpDefault",
                          "Según la configuración de {{client}}. Podés cambiarlo solo para este comprobante.",
                          { client: selectedClient.displayName ?? selectedClient.legalName }
                        )
                      : t(
                          "invoices.create.autoSendEmailOverrideHelpCustom",
                          "Elegido para este comprobante — no cambia la configuración del cliente."
                        )}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Invoice type & recipient fields — hidden when a client is selected since
          the receiver data comes from the client record and all inputs would just be
          read-only echoes. */}
      {cfeType && !hasClientSelected && (
        <Card
          id="invoice-type-card"
          className={
            rucInfoLoading && cfeType === "111" ? "relative" : undefined
          }
        >
          {rucInfoLoading && cfeType === "111" && (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-background/80 backdrop-blur-[1px]"
              aria-live="polite"
              aria-busy="true"
            >
              <div className="flex flex-col items-center gap-3 rounded-md border bg-card px-6 py-4 shadow-sm">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm font-medium text-foreground">
                  {t("clients.form.rucInfoLoading", "Loading company data…")}
                </p>
              </div>
            </div>
          )}
          <CardHeader>
            <CardTitle>
              {cfeType === "121"
                ? t("invoices.create.step3ExportTitle")
                : t("invoices.new.invoiceDetailsCardTitle")}
            </CardTitle>
            {cfeType === "121" && (
              <p className="text-sm text-muted-foreground">
                {t("invoices.create.step3ExportSubtitle")}
              </p>
            )}
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            {!hasClientSelected && (
              <div className="space-y-2 sm:col-span-2">
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button
                    type="button"
                    variant={cfeType === "101" ? "default" : "outline"}
                    className="flex-1"
                    disabled={!caesLoading && !canEmitETicket}
                    title={
                      !caesLoading && !canEmitETicket
                        ? t("invoices.create.noCaeETicket")
                        : undefined
                    }
                    onClick={() => {
                      setClientLocation("uruguay");
                      setNeedRut(false);
                    }}
                  >
                    {t("invoices.create.typeFinalConsumer")}
                  </Button>
                  <Button
                    type="button"
                    variant={cfeType === "111" ? "default" : "outline"}
                    className="flex-1"
                    disabled={!caesLoading && !canEmitEFactura}
                    title={
                      !caesLoading && !canEmitEFactura
                        ? t("invoices.create.noCaeEFactura")
                        : undefined
                    }
                    onClick={() => {
                      setClientLocation("uruguay");
                      setNeedRut(true);
                    }}
                  >
                    {t("invoices.create.typeDomesticInvoice")}
                  </Button>
                </div>
                {!caesLoading && !canEmitETicket && !canEmitEFactura ? (
                  <p className="text-sm text-destructive">
                    {t("invoices.create.noCaeAtAll")}
                  </p>
                ) : !caesLoading && (!canEmitETicket || !canEmitEFactura) ? (
                  <p className="text-sm text-muted-foreground">
                    {!canEmitETicket
                      ? t("invoices.create.noCaeETicket")
                      : t("invoices.create.noCaeEFactura")}
                  </p>
                ) : null}
              </div>
            )}
            {cfeType === "101" && (
              <>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="receiverName">
                    {t("invoices.create.clientNameOptional")}
                  </Label>
                  <Input
                    id="receiverName"
                    value={receiver.name}
                    onChange={(e) =>
                      setReceiver((r) => ({ ...r, name: e.target.value }))
                    }
                    placeholder={t("invoices.new.receiverNamePlaceholder")}
                    disabled={hasClientSelected}
                  />
                </div>
                {/*
                  Optional RUT/C.I. for the Consumidor Final receptor. Always
                  visible (we'd previously tried an opt-in toggle but it added
                  friction for the high-value case). Backend
                  validateEticketConsumidorFinalThreshold throws when this
                  field is blank and the converted total exceeds 5,000 UI.
                */}
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="receiverDocCi">
                    {t("invoices.create.rutOrCiOptional")}
                  </Label>
                  <Input
                    id="receiverDocCi"
                    value={receiver.rut}
                    onChange={(e) =>
                      setReceiver((r) => ({
                        ...r,
                        rut: e.target.value.replace(/\D/g, "").slice(0, 12),
                      }))
                    }
                    inputMode="numeric"
                    placeholder="RUT (12 dígitos) o C.I. (8 dígitos)"
                    maxLength={12}
                    disabled={hasClientSelected}
                  />
                </div>
              </>
            )}
            {cfeType === "111" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="receiverRut">
                    {t("invoices.create.rutRequired")}
                  </Label>
                  <Input
                    id="receiverRut"
                    value={receiver.rut}
                    onChange={(e) =>
                      setReceiver((r) => ({ ...r, rut: e.target.value }))
                    }
                    onBlur={fetchReceiverRucOnBlur}
                    placeholder="120000000012"
                    maxLength={14}
                    className={fieldErrors.receiverRut ? "border-destructive" : ""}
                    disabled={hasClientSelected}
                  />
                  {fieldErrors.receiverRut && (
                    <p className="text-sm text-destructive">
                      {fieldErrors.receiverRut}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="receiverName">
                    {t("invoices.create.companyOrClientName")}
                  </Label>
                  <Input
                    id="receiverName"
                    value={receiver.name}
                    onChange={(e) =>
                      setReceiver((r) => ({ ...r, name: e.target.value }))
                    }
                    className={fieldErrors.receiverName ? "border-destructive" : ""}
                    disabled={hasClientSelected}
                  />
                  {fieldErrors.receiverName && (
                    <p className="text-sm text-destructive">
                      {fieldErrors.receiverName}
                    </p>
                  )}
                </div>
              </>
            )}
            {cfeType === "121" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="country">{t("invoices.create.country")}</Label>
                  <Select
                    value={receiver.countryCode || undefined}
                    onValueChange={(v) =>
                      setReceiver((r) => ({ ...r, countryCode: v }))
                    }
                  >
                    <SelectTrigger
                      id="country"
                      className={fieldErrors.countryCode ? "border-destructive" : ""}
                      disabled={hasClientSelected}
                    >
                      <SelectValue placeholder={t("invoices.create.country")} />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRY_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fieldErrors.countryCode && (
                    <p className="text-sm text-destructive">
                      {fieldErrors.countryCode}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="receiverName">
                    {t("invoices.create.clientName")}
                  </Label>
                  <Input
                    id="receiverName"
                    value={receiver.name}
                    onChange={(e) =>
                      setReceiver((r) => ({ ...r, name: e.target.value }))
                    }
                    className={fieldErrors.receiverName ? "border-destructive" : ""}
                    disabled={hasClientSelected}
                  />
                  {fieldErrors.receiverName && (
                    <p className="text-sm text-destructive">
                      {fieldErrors.receiverName}
                    </p>
                  )}
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="docRecepExt">
                    {t("invoices.create.foreignDocumentOptional")}
                  </Label>
                  <Input
                    id="docRecepExt"
                    value={receiver.docRecepExt}
                    onChange={(e) =>
                      setReceiver((r) => ({
                        ...r,
                        docRecepExt: e.target.value.slice(0, 20),
                      }))
                    }
                    maxLength={20}
                    placeholder="e.g. passport number"
                    disabled={hasClientSelected}
                  />
                </div>
              </>
            )}
            {(cfeType === "101" || cfeType === "111") && (
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="receiverAddress">
                  {t("invoices.new.address")} ({t("invoices.new.optional")})
                </Label>
                <Input
                  id="receiverAddress"
                  value={receiver.address}
                  onChange={(e) =>
                    setReceiver((r) => ({ ...r, address: e.target.value }))
                  }
                  placeholder={t("invoices.new.addressPlaceholder")}
                  disabled={hasClientSelected}
                />
              </div>
            )}
            {cfeType === "111" && (
              <>
                <div className="space-y-2 sm:col-span-1">
                  <Label htmlFor="receiverCiudad">
                    {t("invoices.new.receiverCity", "City")} (
                    {t("invoices.new.optional")})
                  </Label>
                  <Input
                    id="receiverCiudad"
                    value={receiver.ciudad}
                    onChange={(e) =>
                      setReceiver((r) => ({ ...r, ciudad: e.target.value }))
                    }
                    placeholder={t(
                      "invoices.new.receiverCityPlaceholder",
                      "Montevideo"
                    )}
                    disabled={hasClientSelected}
                    maxLength={30}
                    className="transition-colors duration-150"
                  />
                </div>
                <div className="space-y-2 sm:col-span-1">
                  <Label htmlFor="receiverDepto">
                    {t("invoices.new.receiverDepartment", "Department / state")}{" "}
                    ({t("invoices.new.optional")})
                  </Label>
                  <Input
                    id="receiverDepto"
                    value={receiver.departamento}
                    onChange={(e) =>
                      setReceiver((r) => ({ ...r, departamento: e.target.value }))
                    }
                    placeholder={t(
                      "invoices.new.receiverDepartmentPlaceholder",
                      "Montevideo"
                    )}
                    disabled={hasClientSelected}
                    maxLength={30}
                    className="transition-colors duration-150"
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {cfeType && (
        <Card>
          <CardContent className="grid gap-4 pt-6 sm:grid-cols-2">
            <DatePickerField
              id="issueDate"
              value={issueDate}
              onChange={setIssueDate}
              label={t("invoices.create.issueDate")}
              error={fieldErrors.issueDate}
              maxDate={
                isEditingDraft
                  ? undefined
                  : parseLocalDateOnly(tomorrowInUruguay())
              }
            />
            <div className="space-y-2">
              <Label htmlFor="paymentMethod" className="text-sm font-medium">
                {t("invoices.new.paymentMethod", "Forma de pago")}
              </Label>
              <Select
                value={paymentMethod}
                onValueChange={(v) => setPaymentMethod(v as "contado" | "credito")}
              >
                <SelectTrigger id="paymentMethod">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="contado">
                    {t("invoices.list.paymentMethodContado", "Spot")}
                  </SelectItem>
                  <SelectItem value="credito">
                    {t("invoices.list.paymentMethodCredito", "Credit")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {brands.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="brandId" className="text-sm font-medium">
                  {t("invoices.new.brand", "Marca")}
                </Label>
                {/* Shared with the client and API-key forms so the three
                    cannot drift. "No marca" is labelled with the company's own
                    name here, because that is literally what it issues as. */}
                <BrandSelect
                  id="brandId"
                  value={brandId || null}
                  onChange={(v) => {
                    brandTouched.current = true;
                    setBrandId(v ?? "");
                  }}
                  noneLabel={
                    tenant?.legalName?.trim() ||
                    tenant?.name?.trim() ||
                    t("invoices.new.brandNone", "Sin marca")
                  }
                />
                <p className="text-xs text-muted-foreground">
                  {t(
                    "invoices.new.brandHelp",
                    "Cambia el logo y el nombre en la factura. El RUT y la razón social son siempre los de la empresa."
                  )}
                </p>
              </div>
            )}

          </CardContent>
        </Card>
      )}

      {/* Lines (with Create invoice at bottom) */}
      {cfeType && (
        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-4">
            <CardTitle>{t("invoices.new.lines")}</CardTitle>
            <div className="flex flex-wrap items-center gap-3">
              {(cfeType === "101" || cfeType === "111") && (
                <>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="line-currency" className="text-sm font-normal text-muted-foreground whitespace-nowrap">
                      {t("invoices.new.currency")}
                    </Label>
                    <Select
                      value={lineCurrency}
                      onValueChange={(v) =>
                        setLineCurrency(v as "UYU" | "USD")
                      }
                    >
                      <SelectTrigger id="line-currency" className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="UYU">
                          {t("invoices.new.currencyUYU")}
                        </SelectItem>
                        <SelectItem value="USD">
                          {t("invoices.new.currencyUSD")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {lineCurrency === "USD" && (
                    <div className="flex items-center gap-2">
                      <Label htmlFor="exchange-rate" className="text-sm font-normal text-muted-foreground whitespace-nowrap">
                        {t("invoices.new.exchangeRate")}
                      </Label>
                      {/* BCU-sourced, not editable: the rate is the BCU sell
                          rate for the invoice's issueDate; the backend also
                          band-checks it, so a manual override only risks a
                          rejection. */}
                      <Input
                        id="exchange-rate"
                        type="number"
                        className="w-24"
                        value={String(exchangeRate)}
                        readOnly
                        disabled
                        title={t("invoices.new.exchangeRateHelp")}
                      />
                      <span className="text-sm text-muted-foreground">
                        UYU · BCU
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {tenant?.exoneratedRegimeEnabled && tenant?.exoneratedRegimeLegend?.trim() && (
              <div className="rounded-md border border-border/60 bg-muted/30 px-3 py-2">
                <p className="text-xs font-medium text-muted-foreground">
                  {t("invoices.new.regimeLegendHint")}
                </p>
                <p className="mt-1 text-sm text-foreground">
                  {tenant.exoneratedRegimeHeaderLegend?.trim() || tenant.exoneratedRegimeLegend}
                </p>
              </div>
            )}
            {linesForm.map((line, index) => {
              const rate = resolveLineRate(line);
              const priceWithoutVat = isPriceWithoutVat(line.taxCategory);
              const lineTotalWithVat = line.quantity * line.unitPrice * (1 + rate);
              return (
                <div
                  key={index}
                  className="grid w-full grid-cols-2 gap-3 rounded-lg border p-3 sm:flex sm:flex-wrap sm:items-end"
                >
                  <div className="col-span-2 space-y-2 sm:min-w-[10rem] sm:flex-1">
                    {pickerItems.length > 0 && (
                      <InvoiceLineCatalogPicker
                        items={pickerItems}
                        selectedCatalogItemId={line.catalogItemId ?? null}
                        onSelectItem={(item) => applyCatalogItemToLine(index, item)}
                        onClearSelection={() => resetCatalogLineToManual(index)}
                      />
                    )}
                    <div className="space-y-1">
                    <Label className="text-xs whitespace-nowrap">{t("invoices.new.description")}</Label>
                    <Input
                      value={line.description}
                      onChange={(e) =>
                        updateLine(index, {
                          description: e.target.value,
                          catalogItemId: null,
                        })
                      }
                      placeholder={t("invoices.new.descriptionPlaceholder")}
                      className={
                        fieldErrors[`line_${index}_description`]
                          ? "border-destructive"
                          : ""
                      }
                    />
                    {fieldErrors[`line_${index}_description`] && (
                      <p className="text-xs text-destructive">
                        {fieldErrors[`line_${index}_description`]}
                      </p>
                    )}
                    </div>
                  </div>
                  <div className="space-y-1 sm:w-24 sm:shrink-0">
                    <Label className="text-xs whitespace-nowrap">{t("invoices.new.quantity")}</Label>
                    <Input
                      type="number"
                      min={0.001}
                      step="any"
                      value={line.quantity || ""}
                      onChange={(e) =>
                        updateLine(index, {
                          quantity: Number(e.target.value) || 0,
                        })
                      }
                      className={
                        fieldErrors[`line_${index}_quantity`]
                          ? "border-destructive"
                          : ""
                      }
                    />
                    {fieldErrors[`line_${index}_quantity`] && (
                      <p className="text-xs text-destructive">
                        {fieldErrors[`line_${index}_quantity`]}
                      </p>
                    )}
                  </div>
                  {cfeType !== "121" && (
                    <div className="space-y-1 sm:w-[150px] sm:shrink-0">
                      <Label className="text-xs whitespace-nowrap">{t("invoices.new.tasaIva")}</Label>
                      {/* Keyed by OPTION id, not category: an automotora has
                          two vat_other rates (2,2% autos, 2,75% motos) and the
                          category alone cannot tell them apart. */}
                      <Select
                        value={
                          findTaxOptionByCategoryAndRate(
                            line.taxCategory,
                            line.taxRate,
                          )?.id ?? line.taxCategory
                        }
                        disabled={forceExemptTax}
                        onValueChange={(v) => {
                          const opt = availableTaxOptions.find((o) => o.id === v);
                          if (!opt) return;
                          updateLine(index, {
                            taxCategory: opt.category,
                            taxRate: opt.rate,
                          });
                        }}
                      >
                        <SelectTrigger
                          aria-disabled={forceExemptTax || undefined}
                          title={
                            forceExemptTax
                              ? t(
                                  "invoices.new.taxLockedExoneradoTooltip",
                                  "Tu régimen exonerado fija IVA en Exento.",
                                )
                              : undefined
                          }
                        >
                          <SelectValue>
                            {(() => {
                              const opt = findTaxOptionByCategoryAndRate(
                                line.taxCategory,
                                line.taxRate,
                              );
                              if (opt?.id === "automotor_2_2")
                                return t("invoices.new.taxShortAutomotor", "IVA 2,2%");
                              if (opt?.id === "moto_2_75")
                                return t("invoices.new.taxShortMoto", "IVA 2,75%");
                              return line.taxCategory === "exempt" ? t("invoices.new.taxShortExempt", "Exento")
                                : line.taxCategory === "vat_basic" ? t("invoices.new.taxShortVatBasic", "IVA 22%")
                                : line.taxCategory === "vat_minimum" ? t("invoices.new.taxShortVatMinimum", "IVA 10%")
                                : line.taxCategory === "export" ? t("invoices.new.taxShortExport", "Exportación")
                                : t("invoices.new.taxShortNotTaxable", "No gravado");
                            })()}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {availableTaxOptions.map((o) => (
                            <SelectItem key={o.id} value={o.id}>
                              {t(o.labelKey, o.fallback)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-1 sm:w-32 sm:shrink-0">
                    <Label className="text-xs whitespace-nowrap">{t("invoices.new.unitPriceWithoutVat")}</Label>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={line.unitPrice || ""}
                      onChange={(e) =>
                        updateLine(index, {
                          unitPrice: Number(e.target.value) || 0,
                        })
                      }
                      className={
                        fieldErrors[`line_${index}_unitPrice`]
                          ? "border-destructive"
                          : ""
                      }
                    />
                    {fieldErrors[`line_${index}_unitPrice`] && (
                      <p className="text-xs text-destructive">
                        {fieldErrors[`line_${index}_unitPrice`]}
                      </p>
                    )}
                  </div>
                  {cfeType !== "121" && !priceWithoutVat && (
                    <div className="space-y-1 sm:w-32 sm:shrink-0">
                      <Label className="text-xs whitespace-nowrap">{t("invoices.new.amountWithoutVat", "Importe sin IVA")}</Label>
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        value={(line.quantity * line.unitPrice).toFixed(2)}
                        readOnly
                        className="bg-muted"
                      />
                    </div>
                  )}
                  {linesForm.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeLine(index)}
                      className="col-span-2 justify-self-end text-destructive hover:bg-destructive/10 hover:text-destructive transition-colors sm:ml-auto"
                    >
                      {t("invoices.new.removeLine")}
                    </Button>
                  )}
                </div>
              );
            })}
            <div className="flex justify-start pt-1">
              <Button type="button" variant="outline" size="sm" onClick={addLine}>
                {t("invoices.new.addLine")}
              </Button>
            </div>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-end">
              <div className="flex flex-wrap gap-4 text-sm font-medium justify-end">
                <span>{t("invoices.create.subtotal")}: {displayCurrency} {formatMoney(totals.subtotal)}</span>
                <span>{t("invoices.create.tax")}: {displayCurrency} {formatMoney(totals.taxTotal)}</span>
                <span>{t("invoices.create.total")}: {displayCurrency} {formatMoney(totals.total)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Its own card, AFTER Líneas — the user's call, and it matches where
          the text lands: the foot of the invoice, below the line items. It
          used to sit in the details card beside the Marca dropdown, which put
          the data dependency (the marca decides which standing adenda applies)
          ahead of where someone actually looks for it. */}
      {cfeType && (
        <Card>
          {/* ONE CARD, COLLAPSED BY DEFAULT when a fija exists.
              The section used to show three things at once — a textarea, a
              checkbox and the whole bank block — on every invoice, when most
              invoices need none of them: the fija already prints by itself.
              With no fija there is nothing to collapse behind, so the field
              stands alone exactly as before.

              It opens on its own when this invoice ALREADY has something to
              say (a draft's own note, or an edited fija), because a reloaded
              draft that hides its own adenda is the collapse doing harm. */}
          {effectiveStandingAdenda ? (
            <Collapsible open={adendaOpen} onOpenChange={setAdendaOpen}>
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 px-6 py-4 text-left transition-colors duration-150 hover:bg-[var(--fx-s-raised-hover)]"
                >
                  <span className="flex min-w-0 items-baseline gap-3">
                    <span className="shrink-0 font-semibold text-foreground">
                      {t("invoices.new.adendaLabel", "Adenda")}
                    </span>
                    {/* The summary says which of the four states this invoice
                        is in, so the card can be left closed with confidence.
                        Suppressed and edited are called out by name; otherwise
                        it previews the text that will actually print. */}
                    <span className="truncate text-xs text-muted-foreground">
                      {adendaSummary}
                    </span>
                  </span>
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-150",
                      adendaOpen && "rotate-180"
                    )}
                    aria-hidden
                  />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-3 pt-0">
                  {renderAdendaStandingBox()}
                  <label className="flex items-start gap-2 text-sm text-foreground">
                    <Checkbox
                      checked={adendaSuppressDefault}
                      onCheckedChange={(v) => setAdendaSuppressDefault(v === true)}
                    />
                    <span>
                      {t(
                        "invoices.new.adendaSuppress",
                        "No incluir la adenda fija en esta factura"
                      )}
                    </span>
                  </label>
                  <AdendaField
                    id="invoice-adenda"
                    value={adendaText}
                    onChange={setAdendaText}
                    rows={3}
                    placeholder={t(
                      "invoices.new.adendaPlaceholder",
                      "Texto adicional solo para esta factura."
                    )}
                    helper={
                      adendaSuppressDefault
                        ? t(
                            "invoices.new.adendaHelper",
                            "Se imprime en un recuadro rotulado ADENDA al pie de la factura."
                          )
                        : t(
                            "invoices.new.adendaHelperWithStanding",
                            "Se agrega debajo de tu adenda fija, en el recuadro ADENDA al pie de la factura."
                          )
                    }
                  />
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          ) : (
            <>
              <CardHeader>
                <CardTitle>{t("invoices.new.adendaLabel", "Adenda")}</CardTitle>
              </CardHeader>
              <CardContent>
                <AdendaField
                  id="invoice-adenda"
                  value={adendaText}
                  onChange={setAdendaText}
                  rows={3}
                  placeholder={t(
                    "invoices.new.adendaPlaceholder",
                    "Texto adicional solo para esta factura."
                  )}
                  helper={t(
                    "invoices.new.adendaHelper",
                    "Se imprime en un recuadro rotulado ADENDA al pie de la factura."
                  )}
                />
              </CardContent>
            </>
          )}
        </Card>
      )}

      {/* Form actions: Preview + Create invoice (always shown when no success yet so user can re-submit on error) */}
      {!resultId && (
        <Card>
          <CardContent className="flex flex-col gap-2 pt-6 sm:flex-row sm:flex-wrap sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => void handlePreview()}
              disabled={previewPdfLoading || !canCreateInvoice}
              className="w-full sm:w-auto"
            >
              {previewPdfLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("invoices.create.previewInvoiceGenerating")}
                </>
              ) : (
                t("invoices.create.previewInvoice")
              )}
            </Button>
            {/* "Guardar borrador" CREATES a new draft. Editing one already
                has a save action below, and offering both here would let the
                user duplicate the draft they meant to correct. */}
            {!isEditingDraft && (
              <Button
                type="button"
                variant="outline"
                onClick={() => void handleSaveDraft()}
                disabled={savingDraft || submitting}
                className="w-full sm:w-auto"
              >
                {savingDraft ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("invoices.create.savingDraft", "Saving...")}
                  </>
                ) : (
                  t("invoices.create.saveDraft", "Save as draft")
                )}
              </Button>
            )}
            <div className="flex w-full sm:w-auto">
              <Button
                type="button"
                onClick={() => void handleSubmit()}
                disabled={submitting || savingDraft || !canCreateInvoice}
                className="min-w-0 flex-1 justify-center rounded-r-none sm:flex-initial"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isEditingDraft
                      ? t("invoices.create.savingDraft", "Saving...")
                      : t("invoices.create.creating")}
                  </>
                ) : isEditingDraft ? (
                  t("invoices.create.saveDraftChanges", "Guardar cambios")
                ) : (
                  t("invoices.create.createInvoice")
                )}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    size="icon"
                    disabled={submitting || savingDraft || !canCreateInvoice}
                    className="shrink-0 rounded-l-none border-l border-l-primary-foreground/20"
                    aria-label={t("invoices.schedule.scheduleSend", "Programar envío")}
                  >
                    <ChevronDown className="h-4 w-4 translate-y-[1px]" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setScheduleDialogOpen(true)}>
                    <CalendarClock className="mr-2 h-4 w-4" />
                    {t("invoices.schedule.scheduleSend", "Programar envío")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardContent>
          {/*
            Tabla E gate: when an anonymous e-Ticket exceeds UYU 10.000, the
            backend will reject with E05 because TipoDocRecep + DocRecep are
            mandatory above the threshold. Surface the constraint as a clear
            inline alert with two recovery paths: focus the new RUT/C.I.
            input, or flip the form to e-Factura (which forces the full
            receptor identification anyway).
          */}
          {hasClientSelected && !caesLoading && !cfeTypeIssuable && (
            <div className="rounded-b-[var(--fx-r-lg)] border-t border-amber-200 bg-amber-50 px-6 py-4">
              <p className="text-sm font-medium text-amber-900">
                {t("invoices.create.noCaeGateTitle")}
              </p>
              <p className="mt-1 text-sm text-amber-800">
                {cfeType === "111"
                  ? t("invoices.create.noCaeEFactura")
                  : cfeType === "121"
                    ? t("invoices.create.noCaeExport")
                    : t("invoices.create.noCaeETicket")}
              </p>
            </div>
          )}
          {isEticketAnonymousOverThreshold && (
            /*
              Banner lives INSIDE the Card so it visually attaches to the
              action row above (no awkward gap). The Card uses
              rounded-[var(--fx-r-lg)] but Tailwind's rounded-b-* doesn't
              honor that var directly — match it via the same token so the
              amber background gets clipped to the Card's bottom corners
              (previous version had square corners spilling past the radius).
              Border-t-amber-200 keeps the divider from the action row;
              negative bg-clip leaves the Card border intact at the radius.
            */
            <div className="rounded-b-[var(--fx-r-lg)] border-t border-amber-200 bg-amber-50 px-6 py-4">
              <p className="text-sm font-medium text-amber-900">
                {t("invoices.create.thresholdGateTitle")}
              </p>
              <p className="mt-1 text-sm text-amber-800">
                {t("invoices.create.thresholdGateBody", {
                  threshold: Math.round(thresholdUyu).toLocaleString("es-UY"),
                })}
              </p>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const el = document.getElementById("receiverDocCi");
                    if (el instanceof HTMLInputElement) {
                      /* preventScroll suppresses the implicit instant
                       * scroll .focus() does, so the smooth-scroll wins. */
                      el.focus({ preventScroll: true });
                      el.scrollIntoView({ behavior: "smooth", block: "center" });
                    }
                  }}
                >
                  {t("invoices.create.thresholdGateLoadClient")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setClientLocation("uruguay");
                    setNeedRut(true);
                    /* React commits the cfeType flip first; rAF lets the
                     * type-card re-render (it now shows the e-Factura RUT +
                     * Name inputs) before we scroll, so the smooth-scroll
                     * lands on the post-flip layout instead of the old. */
                    requestAnimationFrame(() => {
                      const el = document.getElementById("invoice-type-card");
                      el?.scrollIntoView({ behavior: "smooth", block: "start" });
                    });
                  }}
                >
                  {t("invoices.create.thresholdGateSwitchToEfactura")}
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      <Dialog
        open={zeroConfirmOpen}
        onOpenChange={(open) => {
          if (!open && (submitting || scheduling)) return;
          setZeroConfirmOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t("invoices.create.zeroConfirm.title", "Issue a $0 document")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "invoices.create.zeroConfirm.description",
                "This document totals $0. It will be issued as a free delivery (Entrega Gratuita — no charge, no VAT). Do you want to continue?"
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              disabled={submitting || scheduling}
              onClick={() => {
                setZeroConfirmOpen(false);
                setPendingScheduleSendAt(null);
              }}
            >
              {t("invoices.create.zeroConfirm.cancel", "Cancel")}
            </Button>
            <Button
              type="button"
              disabled={submitting || scheduling}
              onClick={async () => {
                if (pendingScheduleSendAt) {
                  const sendAtIso = pendingScheduleSendAt;
                  setPendingScheduleSendAt(null);
                  await handleScheduleSend(sendAtIso, { confirmedZero: true });
                } else {
                  await handleSubmit({ confirmedZero: true });
                }
                // Both handlers catch + toast their own errors and never
                // throw, and navigate away on success (unmounting this
                // dialog) — closing here only matters on the error path,
                // where staying open behind a stale spinner would be worse.
                setZeroConfirmOpen(false);
              }}
            >
              {submitting || scheduling ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("invoices.create.zeroConfirm.confirming", "Emitiendo...")}
                </>
              ) : (
                t("invoices.create.zeroConfirm.confirm", "Issue at $0")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AddClientDialog
        open={addClientOpen}
        onOpenChange={setAddClientOpen}
        onCreated={(client) => {
          onSelectClient(client.id, client);
        }}
      />

      <ScheduleSendDialog
        open={scheduleDialogOpen}
        onOpenChange={setScheduleDialogOpen}
        submitting={scheduling}
        onConfirm={handleScheduleSend}
      />
    </div>
  );
}
